import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { GlobalService } from 'app/global/global.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { IDocumentTypesDto } from '../../documents/dto/document-listing.dto';
import { IMetadataItemDto } from '../../settings/documentfields/metadata-item.dto';
import { CustomBoxBuilderService } from '../../settings/documenttype/document-type-settings-modal/contract-control-edit-layout/custom-box/custom-box-builder.service';
import { DocumentTypeGroupEnum, DocumentTypeSectionEnum, IBoxTypeEnum } from '../../settings/documenttype/document-type-settings-modal/default-constants/document-type-settings.constants';
import { IField } from '../../settings/documenttype/document-type-settings-modal/document-type-settings.dto';
import { CONTRACT_DETAIL_FACADE_TOKEN, IContractDetailFacade } from '../contract-control-detail-view/store/contract-detail-facade.token';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-contract-control-layout-settings',
  templateUrl: './contract-control-layout-settings.component.html',
  styleUrls: ['./contract-control-layout-settings.component.scss']
})
export class ContractControlLayoutSettingsComponent implements OnInit, OnDestroy {

  @Input() layoutSettings: IDocumentTypesDto;
  @Input() contract_control_id: number;

  public updateLayoutSettingsRequestState$: Observable<boolean> = this._contractFacade.updateLayoutSettingsRequestState$;
  public metaDataItems$: Observable<IMetadataItemDto[]> = this._contractFacade.metaDataItems$;
  public ccLayoutSettings: IDocumentTypesDto;
  public destroy$ = new Subject<void>();


  constructor(
    private _customBoxBuilderService: CustomBoxBuilderService,
    private _globalService: GlobalService,
    @Inject(CONTRACT_DETAIL_FACADE_TOKEN) public _contractFacade: IContractDetailFacade,
    public _activeModal: NgbActiveModal,
  ) { }



  ngOnInit(): void {
    this._contractFacade.fetchMetaDataItems();
    this.ccLayoutSettings = structuredClone(this.layoutSettings);
  }


  private get getActiveSection() {
    return this.ccLayoutSettings.sections.filter(s => s.name === DocumentTypeSectionEnum.ContractControlLayout);
  }





  getCustomBoxOrder(): number {
    const boxOrder = this.getActiveSection
      .reduce((total, section) => total + section.boxes?.filter(x => x.type === IBoxTypeEnum.CUSTOM)?.length, 0);

    return boxOrder + 1;
  }





  removeFieldFromCustomBox($event) {
    this.ccLayoutSettings.sections.filter(s => s.name === DocumentTypeSectionEnum.ContractControlLayout).forEach(section => {
      section.boxes?.forEach(box => {
        if (box.fields) {
          box.fields = box.fields.filter(f => f.meta_data_id !== $event.id);
        }
      });
    });
  }


  createCustomBox(): void {
    const customBox = this._customBoxBuilderService.buildBox({
      id: null,
      name: `Custom box ${this.getCustomBoxOrder()}`,
      order: this.getCustomBoxOrder(),
      is_removeable: true,
      type: IBoxTypeEnum.CUSTOM,
      is_locked: false,
      status: true,
    });

    const customBoxName = customBox.name;
    const boxAlreadyExists = this.ccLayoutSettings.sections.filter(s => s.name === DocumentTypeSectionEnum.ContractControlLayout).some(section =>
      section.boxes?.some(box => box.name === customBoxName && box.type === IBoxTypeEnum.CUSTOM)
    );

    if (boxAlreadyExists) {
      this._globalService.toastrError('A box with this name already exists!');
      return;
    }

    this.ccLayoutSettings.sections.filter(s => s.name === DocumentTypeSectionEnum.ContractControlLayout).forEach(section => {
      const clonedBoxes = structuredClone(section.boxes ?? []);
      clonedBoxes.push(structuredClone(customBox));
      section.boxes = clonedBoxes;
    });
  }





  removeCustomBox($event: number): void {
    this.ccLayoutSettings.sections.filter(s => s.name === DocumentTypeSectionEnum.ContractControlLayout).forEach(section => {
      section.boxes = section.boxes.filter((_, index) => index !== $event);
    });
  }



  /**
   * Handles the drop event for rearranging contract control layout boxes via drag-and-drop.
   * 
   * This method clones the current section's boxes, reorders them based on the drag event,
   * updates their order property, and then updates the state with the new box order.
   *
   * @param event - The drag-and-drop event containing previous and current indices of the moved item.
   */
  drop(event: CdkDragDrop<string[]>): void {
    const sections = this.ccLayoutSettings.sections.find(s => s.name === DocumentTypeSectionEnum.ContractControlLayout)
    let boxes = structuredClone(sections.boxes);
    moveItemInArray(boxes, event.previousIndex, event.currentIndex);
    boxes.forEach((box, index) => {
      box.order = index;
    });
    this.ccLayoutSettings.sections.find(s => s.name === DocumentTypeSectionEnum.ContractControlLayout).boxes = boxes;
  }




  addFieldsInCustomBox($event: { boxIndex: number, field: IMetadataItemDto }): void {
    this.ccLayoutSettings.sections.filter(s => s.name === DocumentTypeSectionEnum.ContractControlLayout).forEach(section => {
      const box = section.boxes?.[$event.boxIndex];
      if (box) {
        box.fields = [...(box.fields || []), this._customBoxBuilderService.buildCustomField($event.field, $event.field.meta_data_type)] as IField[];
      }
    });
  }




  updateFieldsOrders($event: { nestedTab: DocumentTypeGroupEnum, fields: IField[] }): void {
    this.ccLayoutSettings.sections.filter(s => s.name === DocumentTypeSectionEnum.ContractControlLayout).forEach(section => {
      section.groups.forEach(group => {
        if (group.name === DocumentTypeGroupEnum.Overview) {
          group.fields = $event.fields;
        }
      });
    });
  }





  /**
   * Updates the visibility status of a specific box in the layout.
   *
   * @param $event - An object containing the index of the box (`boxIndex`) and its new visibility status (`isVisible`).
   */
  updateVisibility($event: { boxIndex: number, isVisible: boolean }): void {
    this.ccLayoutSettings.sections.filter(s => s.name === DocumentTypeSectionEnum.ContractControlLayout).forEach(section => {
      section.boxes = section.boxes.map((box, index) => {
        if (index === $event.boxIndex) {
          return {
            ...box,
            status: $event.isVisible
          };
        }
        return box;
      });
    });
  }




  updateLayoutSettings() {
    this._contractFacade.updateLayoutSettings(this.ccLayoutSettings, this.contract_control_id);
    this.updateLayoutSettingsRequestState$.pipe(
      takeUntil(this.destroy$),
    ).subscribe((isLoading) => {
      if (!isLoading) {
        this._activeModal.close();
      }
    });
  }



  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
